import { Temporal } from 'temporal-polyfill'

/**
 * @param date - The date to format
 * @param minimalCutOff - The minimal cutoff to use (e.g. 2 count in hours instead of minutes)
 * @returns The formatted relative time
 */
export const formatRelativeTime = (
  date: Date | number,
  minimalCutOff = 0
): string => {
  const timeMs = typeof date === 'number' ? date : date.getTime()

  const deltaSeconds = Math.floor((timeMs - Date.now()) / 1000)
  const lang = 'fr-FR'

  const cutoffs = [
    60, // 1 minute
    3600, // 1 hour
    86400, // 1 day
    604800, // 1 week
    2592000, // 1 month
    31536000, // 1 year
    Number.POSITIVE_INFINITY
  ]

  const units: Intl.RelativeTimeFormatUnit[] = [
    'second',
    'minute',
    'hour',
    'day',
    'week',
    'month',
    'year'
  ]

  let unitIndex = cutoffs.findIndex((cutoff) => cutoff > Math.abs(deltaSeconds))

  if (unitIndex === -1 || unitIndex < minimalCutOff) {
    unitIndex = minimalCutOff
  }

  let unit: number
  // Special handling for day-level comparisons to ensure unit is full day not an interval
  if (units[unitIndex] === 'day') {
    const inputDate = new Date(timeMs)
    const today = new Date()

    // Reset time to start of day for proper day comparison
    const inputDay = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    )
    const todayDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    )

    const dayDiff = Math.floor(
      (inputDay.getTime() - todayDay.getTime()) / 86400000
    )
    unit = dayDiff
  } else {
    const divisor = unitIndex > 0 ? (cutoffs[unitIndex - 1] as number) : 1
    unit = Math.floor(deltaSeconds / divisor)
  }

  // Validate that unit is a finite number before formatting
  if (!Number.isFinite(unit)) {
    return ''
  }

  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })
  return rtf
    .format(unit, units[unitIndex] as Intl.RelativeTimeFormatUnit)
    .replace('minutes', 'min')
}

export const toISOLocal = (d: Date) => {
  const z = (n: number) => `0${n}`.slice(-2)
  const zz = (n: number) => `00${n}`.slice(-3)
  let off = d.getTimezoneOffset()
  const sign = off > 0 ? '-' : '+'
  off = Math.abs(off)

  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}:${z(d.getSeconds())}.${zz(d.getMilliseconds())}${sign}${z((off / 60) | 0)}:${z(off % 60)}`
}

export const formatDateShort = (date: Date): string => {
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

export const parseDateOrNull = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = value instanceof Date ? value : new Date(String(value))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const formatTemporalShort = (
  date: Temporal.ZonedDateTime | null,
  defaultValue: string | null = null
) =>
  date?.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }) ?? defaultValue

export const formatDateTimeShort = (date: Date) => {
  const day = `${date.getDate()}`.padStart(2, '0')
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const year = `${date.getFullYear()}`
  const hour = `${date.getHours()}`.padStart(2, '0')
  const minute = `${date.getMinutes()}`.padStart(2, '0')
  return `${day}/${month}/${year} à ${hour}:${minute}`
}

export const formatLocaleNullableDate = (
  dateString: string | null
): string | null => {
  if (!dateString) return null
  const date = new Date(dateString)
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR')
}

export const formatDuration = (start: Date, end: Date | null): string => {
  if (!end) return ''
  const diffMs = end.getTime() - start.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  if (diffMinutes < 60) {
    return `${diffMinutes} min`
  }
  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60
  return minutes > 0 ? `${hours}h${minutes}` : `${hours}h`
}

export const formatDurationSeconds = (seconds: number | null): string => {
  if (seconds === null) {
    return '-'
  }

  const duration = Temporal.Duration.from({
    seconds: Math.round(seconds)
  }).round({ largestUnit: 'days' })

  const parts: string[] = []
  if (duration.days > 0) {
    parts.push(`${duration.days} j`)
  }
  if (duration.hours > 0) {
    parts.push(`${duration.hours} h`)
  }
  if (duration.days === 0 && duration.minutes > 0) {
    parts.push(`${duration.minutes} min`)
  }

  return parts.length > 0 ? parts.join(' ') : '< 1 min'
}

export const formatTime = (date: Date) => {
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const formatDateTime = (
  date: Date,
  options?: Intl.DateTimeFormatOptions
) => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    dateStyle: 'long',
    timeStyle: 'short'
  }

  return new Intl.DateTimeFormat('fr-FR', {
    ...defaultOptions,
    ...options
  }).format(date)
}

export type RelativeDateUnit = 'hour' | 'day' | 'week' | 'month' | 'year'

const relativeDateUnitLabels: Record<
  RelativeDateUnit,
  (count: number) => string
> = {
  hour: (count) => (count > 1 ? 'heures' : 'heure'),
  day: (count) => (count > 1 ? 'jours' : 'jour'),
  week: (count) => (count > 1 ? 'semaines' : 'semaine'),
  month: () => 'mois',
  year: (count) => (count > 1 ? 'années' : 'année')
}

/**
 * Builds an ISO 8601 duration string (eg. `P6M`) representing an offset in the
 * past, used to store relative dates in query params / saved searches.
 */
export const formatRelativeDateValue = (
  count: number,
  unit: RelativeDateUnit
): string => Temporal.Duration.from({ [`${unit}s`]: count }).toString()

/** Parses an ISO 8601 duration value back into a count + unit, or null. */
export const parseRelativeDate = (
  value: string
): { count: number; unit: RelativeDateUnit } | null => {
  if (!value.startsWith('P')) return null
  let duration: Temporal.Duration
  try {
    duration = Temporal.Duration.from(value)
  } catch {
    return null
  }
  if (duration.years) return { count: duration.years, unit: 'year' }
  if (duration.months) return { count: duration.months, unit: 'month' }
  if (duration.weeks) return { count: duration.weeks, unit: 'week' }
  if (duration.days) return { count: duration.days, unit: 'day' }
  if (duration.hours) return { count: duration.hours, unit: 'hour' }
  return null
}

export const isRelativeDateValue = (value: string): boolean =>
  parseRelativeDate(value) !== null

const isDateOnlyValue = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value)

/**
 * The API's `before` bound is exclusive of the day itself for date-only
 * fields, so the end date is shifted one day forward to include it — `after`
 * is already inclusive and needs no adjustment.
 */
export const resolveDateRangeBound = (
  value: string,
  bound: 'after' | 'before'
): string => {
  const resolved = isRelativeDateValue(value)
    ? Temporal.Now.plainDateISO()
        .subtract(Temporal.Duration.from(value))
        .toString()
    : value

  if (!isDateOnlyValue(resolved) || bound === 'after') {
    return resolved
  }

  return Temporal.PlainDate.from(resolved).add({ days: 1 }).toString()
}

export const formatRelativeDateLabel = (
  count: number,
  unit: RelativeDateUnit
): string => `${count} ${relativeDateUnitLabels[unit](count)}`

/** « lundi 8 septembre 2026 14:05 », for tooltips on relative or short times */
export const formatFullDateTime = (date: Date): string =>
  date.toLocaleString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })

export const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()
